import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Times from '@decisiv/iconix/lib/components/Times';
import Refresh from '@decisiv/iconix/lib/components/Refresh';
import ArrowUp from '@decisiv/iconix/lib/components/ArrowUp';
import InfoCircle from '@decisiv/iconix/lib/components/InfoCircle';
import { H2 } from '@decisiv/ui-components/lib/components/Typography';
import { ToolkitButton } from './style';

// Flatten a JSON schema into a list of { path, type, description, required }.
// Walks from the schema root so the JSONAPI envelope (data.type, relationships)
// is part of the diff rather than being dropped.
function flattenSchema(schema) {
  const out = [];
  if (!schema || typeof schema !== 'object') return out;

  const rootProps = schema?.properties ?? {};
  const rootRequired = schema?.required ?? [];

  const walk = (obj, prefix, requiredKeys) => {
    Object.entries(obj || {}).forEach(([k, sub]) => {
      const path = prefix ? `${prefix}.${k}` : k;
      let type;
      if (Array.isArray(sub.type)) type = sub.type.join('|');
      else if (sub.type) type = sub.type;
      else type = sub.properties ? 'object' : 'any';
      out.push({
        path,
        type,
        description: sub.description || '',
        required: requiredKeys.includes(k),
        format: sub.format,
      });
      if (sub.properties) {
        walk(sub.properties, path, sub.required || []);
      } else if (
        type === 'array' &&
        sub.items?.type === 'object' &&
        sub.items?.properties
      ) {
        walk(sub.items.properties, `${path}[]`, sub.items.required || []);
      } else if (type === 'array' && sub.items) {
        out.push({
          path: `${path}[]`,
          type: sub.items.type || 'any',
          description: sub.items.description || '',
          required: false,
        });
      }
    });
  };
  walk(rootProps, '', rootRequired);
  return out;
}

function diffFlat(fromList, toList) {
  const fromMap = new Map(fromList.map((p) => [p.path, p]));
  const toMap = new Map(toList.map((p) => [p.path, p]));
  const added = [];
  const removed = [];
  const changed = [];
  const unchanged = [];

  toMap.forEach((toP, path) => {
    const fromP = fromMap.get(path);
    if (!fromP) {
      added.push(toP);
      return;
    }
    const typeDiff = fromP.type !== toP.type;
    const reqDiff = fromP.required !== toP.required;
    const descDiff = (fromP.description || '') !== (toP.description || '');
    if (typeDiff || reqDiff || descDiff) {
      changed.push({ path, from: fromP, to: toP, typeDiff, reqDiff, descDiff });
    } else {
      unchanged.push(toP);
    }
  });
  fromMap.forEach((fromP, path) => {
    if (!toMap.has(path)) removed.push(fromP);
  });

  return { added, removed, changed, unchanged, fromMap, toMap };
}

function DiffLine({ prop = null, kind }) {
  const cls = {
    add: 'diff-add',
    remove: 'diff-remove',
    change: 'diff-change',
    same: 'diff-same',
    empty: 'diff-empty',
  }[kind];

  return (
    <div className={`at-diff-line ${cls}`}>
      <span className="at-diff-gutter">
        {kind === 'add' && '+'}
        {kind === 'remove' && '−'}
        {kind === 'change' && '~'}
      </span>
      <span className="at-diff-body">
        {prop ? (
          <>
            <span className="at-diff-path">
              {prop.path}
              {prop.required && (
                <span className="at-diff-req" title="Required">
                  *
                </span>
              )}
            </span>
            <span className="at-diff-type">{prop.type}</span>
            {prop.description && (
              <span className="at-diff-desc">{prop.description}</span>
            )}
          </>
        ) : (
          <span className="at-diff-blank">—</span>
        )}
      </span>
    </div>
  );
}
DiffLine.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  prop: PropTypes.object,
  kind: PropTypes.oneOf(['add', 'remove', 'change', 'same', 'empty'])
    .isRequired,
};

function renderSideBySide(fromList, toList) {
  const diff = diffFlat(fromList, toList);
  const seen = new Set();
  const rows = [];

  fromList.forEach((p) => {
    seen.add(p.path);
    if (diff.toMap.has(p.path)) {
      const toP = diff.toMap.get(p.path);
      const ch = diff.changed.find((c) => c.path === p.path);
      rows.push({ from: p, to: toP, kind: ch ? 'change' : 'same' });
    } else {
      rows.push({ from: p, to: null, kind: 'remove' });
    }
  });
  toList.forEach((p) => {
    if (!seen.has(p.path)) {
      rows.push({ from: null, to: p, kind: 'add' });
      seen.add(p.path);
    }
  });
  return { rows, diff };
}

function SchemaDiffBlock({ label, fromSchema, toSchema, fromV, toV }) {
  const fromList = useMemo(() => flattenSchema(fromSchema), [fromSchema]);
  const toList = useMemo(() => flattenSchema(toSchema), [toSchema]);
  const { rows, diff } = useMemo(
    () => renderSideBySide(fromList, toList),
    [fromList, toList],
  );

  const totals = {
    added: diff.added.length,
    removed: diff.removed.length,
    changed: diff.changed.length,
  };
  const noChange =
    totals.added === 0 && totals.removed === 0 && totals.changed === 0;

  return (
    <div className="at-diff-block">
      <div className="at-diff-block-head">
        <span className="label">{label}</span>
        <div className="at-diff-totals">
          {noChange ? (
            <span className="at-diff-total-same">No changes</span>
          ) : (
            <>
              {totals.added > 0 && (
                <span className="at-diff-total at-diff-total-add">
                  +{totals.added} added
                </span>
              )}
              {totals.removed > 0 && (
                <span className="at-diff-total at-diff-total-remove">
                  −{totals.removed} removed
                </span>
              )}
              {totals.changed > 0 && (
                <span className="at-diff-total at-diff-total-change">
                  ~{totals.changed} changed
                </span>
              )}
            </>
          )}
        </div>
      </div>
      <div className="at-diff-grid">
        <div className="at-diff-col-head">v{fromV}</div>
        <div className="at-diff-col-head">v{toV}</div>
        {rows.map((r, idx) => {
          const fromKind = r.kind === 'add' ? 'empty' : r.kind;
          const toKind = r.kind === 'remove' ? 'empty' : r.kind;
          return (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={idx}>
              <DiffLine prop={r.from} kind={fromKind} />
              <DiffLine prop={r.to} kind={toKind} />
            </React.Fragment>
          );
        })}
        {rows.length === 0 && (
          <div
            style={{
              gridColumn: '1 / -1',
              padding: 20,
              color: 'var(--if-ink-2)',
              textAlign: 'center',
            }}
          >
            No properties in either version.
          </div>
        )}
      </div>
    </div>
  );
}

SchemaDiffBlock.propTypes = {
  label: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  fromSchema: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  toSchema: PropTypes.object.isRequired,
  fromV: PropTypes.number.isRequired,
  toV: PropTypes.number.isRequired,
};

const hasSchemaContent = (schema) =>
  Boolean(schema) && Object.keys(schema).length > 0;

export default function VersionDiffModal({
  action,
  fromVersion,
  toVersion,
  onClose,
  onMigrate = null,
}) {
  const versions = useMemo(
    () => [...action.versions].sort((a, b) => a.version - b.version),
    [action],
  );
  const [fromV, setFromV] = useState(fromVersion);
  const [toV, setToV] = useState(toVersion);
  const fromObj = action.versions.find((v) => v.version === fromV);
  const toObj = action.versions.find((v) => v.version === toV);

  const swap = () => {
    setFromV(toV);
    setToV(fromV);
  };

  const canMigrate =
    !!onMigrate && fromV < toV && fromObj && fromObj.integrations.length > 0;

  // Custom modal shell (not KDS Modal): the side-by-side schema diff needs a
  // wider, full-bleed canvas than KDS Modal's max width (extraLarge ~900px)
  // allows. Renders inside .at-root so the helper classes resolve directly.
  return (
    <div className="at-modal-overlay" onClick={onClose} role="presentation">
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className="at-modal wide diff-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Compare versions"
      >
        <div className="modal-head">
          <H2>
            Compare versions
            <span
              className="muted small"
              style={{ marginLeft: 8, fontWeight: 400 }}
            >
              <code className="mono">{action.name}</code>
            </span>
          </H2>
          <button type="button" className="modal-close" onClick={onClose}>
            <Times size="small" />
          </button>
        </div>

        <div className="at-diff-controls">
          <div className="at-diff-picker">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="diff-from">From</label>
            <select
              id="diff-from"
              value={fromV}
              onChange={(e) => setFromV(Number(e.target.value))}
            >
              {versions.map((v) => (
                <option key={v.version} value={v.version}>
                  v{v.version}
                  {v.isCurrent ? ' (current)' : ''}
                  {v.locked ? ' · locked' : ''}
                </option>
              ))}
            </select>
          </div>
          <ToolkitButton
            variant="ghost"
            size="small"
            icon={Refresh}
            onClick={swap}
            aria-label="Swap"
          />
          <div className="at-diff-picker">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="diff-to">To</label>
            <select
              id="diff-to"
              value={toV}
              onChange={(e) => setToV(Number(e.target.value))}
            >
              {versions.map((v) => (
                <option key={v.version} value={v.version}>
                  v{v.version}
                  {v.isCurrent ? ' (current)' : ''}
                  {v.locked ? ' · locked' : ''}
                </option>
              ))}
            </select>
          </div>
          <span className="spacer" />
          <span className="muted small">
            {fromObj?.integrations?.length ?? 0} →{' '}
            {toObj?.integrations?.length ?? 0} integrations
          </span>
        </div>

        <div className="modal-body">
          {fromV === toV ? (
            <div className="empty-state" style={{ margin: 0 }}>
              <div className="empty-icon">
                <InfoCircle size="medium" />
              </div>
              <div className="empty-title">
                Pick two different versions to compare.
              </div>
            </div>
          ) : (
            <>
              {toObj?.changeNote && fromV < toV && (
                <div className="at-diff-changenote">
                  <InfoCircle size="small" />
                  <span>
                    <strong>v{toV} change note:</strong> {toObj.changeNote}
                  </span>
                </div>
              )}
              <SchemaDiffBlock
                label="Input schema"
                fromSchema={fromObj?.inputSchema || {}}
                toSchema={toObj?.inputSchema || {}}
                fromV={fromV}
                toV={toV}
              />
              <SchemaDiffBlock
                label="Output schema"
                fromSchema={fromObj?.outputSchema || {}}
                toSchema={toObj?.outputSchema || {}}
                fromV={fromV}
                toV={toV}
              />
              {(hasSchemaContent(fromObj?.metadataSchema) ||
                hasSchemaContent(toObj?.metadataSchema)) && (
                <SchemaDiffBlock
                  label="Integration metadata schema"
                  fromSchema={fromObj?.metadataSchema || {}}
                  toSchema={toObj?.metadataSchema || {}}
                  fromV={fromV}
                  toV={toV}
                />
              )}
            </>
          )}
        </div>

        <div className="modal-foot">
          <ToolkitButton variant="ghost" text="Close" onClick={onClose} />
          <span className="spacer" />
          {canMigrate && (
            <ToolkitButton
              kind="primary"
              icon={ArrowUp}
              text={`Migrate ${fromObj.integrations.length} to v${toV}`}
              onClick={() => onMigrate(fromV, toV)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
VersionDiffModal.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  action: PropTypes.object.isRequired,
  fromVersion: PropTypes.number.isRequired,
  toVersion: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onMigrate: PropTypes.func,
};
